import { AuthTokenStorageService } from '@/lib/auth';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { toast } from 'react-toastify';
import {
  fetchCvAiCareer,
  fetchCvAiCareerHistory,
  fetchCvAiEnhancePhoto,
  fetchCvAiSuggest,
  fetchCvAiSuggestHistory,
  fetchCvAiUsage,
  uploadCvAi,
} from '@/features/cv/api/ai.api';
import { aiQueryKeys } from '@/features/cv/api/ai.keys';
import { cvQueryKeys } from '@/features/cv/api/cv.keys';
import { getMutationErrorMessage } from '@/lib/api';
import { IListResponse } from '@/shared/base/base.dto';
import { CvAiCareerTrackDto } from '@/shared/cv';

export const useCvAiUsage = (enabled = true) => {
  const token = AuthTokenStorageService.getAccessToken();

  return useQuery({
    queryKey: aiQueryKeys.cvAiUsage,
    queryFn: fetchCvAiUsage,
    enabled: Boolean(token) && enabled,
  });
};

export const useCvAiCareerHistory = () => {
  const token = AuthTokenStorageService.getAccessToken();

  return useQuery<IListResponse<CvAiCareerTrackDto>>({
    queryKey: aiQueryKeys.cvAiCareerHistory,
    queryFn: fetchCvAiCareerHistory,
    enabled: !!token,
  });
};

export const useCvAiSuggestHistory = () => {
  const token = AuthTokenStorageService.getAccessToken();

  return useQuery({
    queryKey: aiQueryKeys.cvAiSuggestHistory,
    queryFn: fetchCvAiSuggestHistory,
    enabled: !!token,
  });
};

export const useGenerateCvAiCareer = () => {
  return useMutation({
    mutationFn: (lang: number) => fetchCvAiCareer(lang),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: aiQueryKeys.cvAiCareerHistory,
      });
      queryClient.invalidateQueries({ queryKey: aiQueryKeys.cvAiUsage });
    },
    onError: (error: unknown) => {
      toast.error(getMutationErrorMessage(error));
    },
  });
};

export const useGenerateCvAiSuggest = () => {
  return useMutation({
    mutationFn: (lang: number) => fetchCvAiSuggest(lang),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: aiQueryKeys.cvAiSuggestHistory,
      });
      queryClient.invalidateQueries({ queryKey: aiQueryKeys.cvAiUsage });
    },
    onError: (error: unknown) => {
      toast.error(getMutationErrorMessage(error));
    },
  });
};

export const useCvAiEnhancePhoto = () => {
  return useMutation({
    mutationFn: fetchCvAiEnhancePhoto,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiQueryKeys.cvAiUsage });
    },
  });
};

export const useUploadCvAi = () => {
  return useMutation({
    mutationFn: uploadCvAi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cvQueryKeys.myCv });
    },
    onError: (error: unknown) => {
      toast.error(getMutationErrorMessage(error));
    },
  });
};
